import React, { useEffect, useState } from 'react';
import { MapPin, Globe, Building2, Store, Users, X, ExternalLink, MessageCircle, Image, Film } from "lucide-react";
import Cookies from 'js-cookie';
import { Exhibitor } from "./types";
import { CategoryBadge } from './CategoryBadge';
import { exhibitorsApi } from '@portal/public_event/api/exhibitors';
import { useCurrentUser } from '@portal/hooks/useCurrentUser';

interface ExpoDetailsModalProps {
  exhibitor: Exhibitor;
  onClose: () => void;
  primaryColor?: string;
  ModalWrapper: React.ComponentType<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }>;
  onChatClick?: () => void;
  onCtaTrack?: () => void;
  eventSlug: string;
}

interface MediaContent {
  hasImage: boolean;
  hasVideo: boolean;
  hasEmbed: boolean;
}

export const ExpoDetailsModal: React.FC<ExpoDetailsModalProps> = ({
  exhibitor,
  onClose,
  primaryColor = "#2563eb",
  ModalWrapper,
  onChatClick,
  onCtaTrack,
  eventSlug
}) => {
  const [activeTab, setActiveTab] = useState<'about' | 'media' | 'contact'>('about');
  const [mediaContent, setMediaContent] = useState<MediaContent>({ hasImage: false, hasVideo: false, hasEmbed: false });
  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [ctaClicked, setCtaClicked] = useState(exhibitor.ctaAlreadyClicked || false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const { currentUser, isLoading: userLoading } = useCurrentUser();

  // Ensure backward compatibility
  const companyName = exhibitor.companyName || exhibitor.name;


  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  // Detect media content
  useEffect(() => {
    if (exhibitor.description) {
      const temp = document.createElement('div');
      temp.innerHTML = exhibitor.description;

      const hasImage = temp.querySelectorAll('img, picture, svg').length > 0;
      const hasVideo = temp.querySelectorAll('video, iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="wistia"], iframe[src*="loom"]').length > 0;
      const hasEmbed = temp.querySelectorAll('iframe, embed, object').length > 0 && !hasVideo;

      setMediaContent({ hasImage, hasVideo, hasEmbed });
    }
  }, [exhibitor.description]);

  const handleCtaClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    // For track-only CTAs that have already been clicked, do nothing
    if (exhibitor.ctaTrackOnly && ctaClicked) {
      return;
    }

    // Check if user is authenticated for track-only CTAs
    if (exhibitor.ctaTrackOnly && !currentUser && !userLoading) {
      // Set cookie with current page URL for redirect after sign-in
      const returnPath = window.location.pathname + window.location.search;
      Cookies.set('stored_account_location', returnPath, {
        path: '/',
        expires: 1/24, // 1 hour
        sameSite: 'Lax'
      });
      setShowSignInPrompt(true);
      return;
    }

    setCtaLoading(true);

    try {
      // Always track the CTA click (for both track-only and external link CTAs)
      const response = await exhibitorsApi.trackCtaClick(eventSlug, exhibitor.id);

      if (exhibitor.ctaTrackOnly) {
        // For track-only CTAs, update the clicked state
        setCtaClicked(true);
        // Call parent callback if provided
        if (onCtaTrack) {
          onCtaTrack();
        }
      } else if (exhibitor.ctaLink) {
        // For external link CTAs, open in new tab after tracking
        window.open(exhibitor.ctaLink, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      console.error('Error tracking CTA click:', error);
      if (error.status === 401) {
        // User not authenticated - set redirect cookie and show sign-in prompt
        const returnPath = window.location.pathname + window.location.search;
        Cookies.set('stored_account_location', returnPath, {
          path: '/',
          expires: 1/24, // 1 hour
          sameSite: 'Lax'
        });
        setShowSignInPrompt(true);
      } else {
        // If tracking fails but it's an external link, still open it
        if (!exhibitor.ctaTrackOnly && exhibitor.ctaLink) {
          window.open(exhibitor.ctaLink, '_blank', 'noopener,noreferrer');
        }
      }
    } finally {
      setCtaLoading(false);
    }
  };

  // Extract media elements from description
  const extractMediaElements = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const mediaElements: { type: string; element: string }[] = [];

    // Extract images
    temp.querySelectorAll('img').forEach(img => {
      mediaElements.push({ type: 'image', element: img.outerHTML });
    });

    // Extract videos and iframes
    temp.querySelectorAll('video, iframe').forEach(media => {
      mediaElements.push({ type: 'video', element: media.outerHTML });
    });

    return mediaElements;
  };

  const mediaElements = exhibitor.description ? extractMediaElements(exhibitor.description) : [];
  const hasMediaTab = mediaContent.hasImage || mediaContent.hasVideo || mediaContent.hasEmbed;
  const hasLogo = exhibitor.logo && (exhibitor.logo.url || exhibitor.logo.smallUrl || exhibitor.logo.mediumUrl || exhibitor.logo.largeUrl);
  const hasBanner = exhibitor.banner && (exhibitor.banner.url || exhibitor.banner.mediumUrl || exhibitor.banner.largeUrl);

  return (
    <ModalWrapper isOpen={true} onClose={onClose}>
      <div className="space-y-6 max-w-3xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg transition-colors"
          aria-label="Close modal"
        >
          <X size={20} className="text-gray-600" />
        </button>

        {/* Banner Image - Only show if we have an image */}
        {(hasBanner || (hasLogo && !logoError)) && !bannerError && (
          <div className="aspect-[2/1] rounded-lg overflow-hidden bg-gray-100 mb-0 flex items-center justify-center">
            <img
              src={exhibitor.banner.largeUrl || exhibitor.banner.url || exhibitor.logo.largeUrl || exhibitor.logo.url}
              alt={`${companyName} banner`}
              className="w-full h-full object-cover"
              onError={() => setBannerError(true)}
            />
          </div>
        )}

        <div className="px-2 pb-4 space-y-4">
          {/* Company Info Section */}
          <div className="flex items-center gap-4">
            {/* Logo Box */}
            <div className="flex items-center justify-center p-2 bg-gray-50 rounded-lg w-24 h-24">
              {hasLogo && !logoError ? (
                <img
                  src={exhibitor.logo.mediumUrl || exhibitor.logo.url}
                  alt={companyName}
                  className="w-20 h-20 object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Store size={40} className="text-gray-400" />
              )}
            </div>

            {/* Company Details */}
            <div className="flex-1">
              <h2 className="text-2xl font-semibold">{companyName}</h2>
              <div className="mt-1">
                {/* Categories row */}
                {((exhibitor.universalCategories && exhibitor.universalCategories.length > 0) || 
                  ((!exhibitor.universalCategories || exhibitor.universalCategories.length === 0) && exhibitor.category)) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Show universal categories */}
                    {exhibitor.universalCategories && exhibitor.universalCategories.length > 0 && (
                      <>
                        {exhibitor.universalCategories.map(category => (
                          <CategoryBadge key={category.id} category={category} size="md" />
                        ))}
                      </>
                    )}
                    {/* Fallback to legacy category if no universal categories */}
                    {(!exhibitor.universalCategories || exhibitor.universalCategories.length === 0) && exhibitor.category && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        {exhibitor.category}
                      </span>
                    )}
                  </div>
                )}
                {/* Booth location on new line */}
                {exhibitor.boothLocation && (
                  <div className="flex items-center gap-1 text-gray-600 mt-1">
                    <MapPin size={16} />
                    <span className="font-medium text-sm">{exhibitor.boothLocation}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Primary Actions */}
          <div className="flex flex-wrap gap-2">
            {exhibitor.website && (
              <a
                href={exhibitor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Globe size={18} />
                <span className="font-medium">Visit Website</span>
              </a>
            )}
            {exhibitor.chatEnabled && onChatClick && (
              <button
                onClick={onChatClick}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MessageCircle size={18} />
                <span className="font-medium">Start Chat</span>
              </button>
            )}
            {exhibitor.ctaText && (
              <button
                onClick={handleCtaClick}
                disabled={ctaLoading || (exhibitor.ctaTrackOnly && ctaClicked)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors ${
                  ctaLoading || (exhibitor.ctaTrackOnly && ctaClicked) ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                style={{
                  backgroundColor: exhibitor.ctaTrackOnly && ctaClicked ? '#6b7280' : primaryColor,
                  '--tw-bg-opacity': 1
                }}
                onMouseEnter={(e) => {
                  if (!ctaLoading && !(exhibitor.ctaTrackOnly && ctaClicked)) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <span className="font-medium">
                  {ctaLoading ? 'Processing...' :
                   exhibitor.ctaTrackOnly && ctaClicked ? exhibitor.ctaClickedText :
                   exhibitor.ctaText}
                </span>
                {!ctaLoading && !exhibitor.ctaTrackOnly && <ExternalLink size={18} />}
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('about')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'about'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={{
                  borderColor: activeTab === 'about' ? primaryColor : undefined,
                  color: activeTab === 'about' ? primaryColor : undefined
                }}
              >
                About
              </button>
              {hasMediaTab && (
                <button
                  onClick={() => setActiveTab('media')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === 'media'
                      ? 'text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: activeTab === 'media' ? primaryColor : undefined,
                    color: activeTab === 'media' ? primaryColor : undefined
                  }}
                >
                  Media
                  <span className="flex items-center gap-1">
                    {mediaContent.hasImage && <Image size={14} />}
                    {mediaContent.hasVideo && <Film size={14} />}
                  </span>
                </button>
              )}
              {(exhibitor.contact || exhibitor.socialLinks?.twitter || exhibitor.socialLinks?.linkedin || exhibitor.socialLinks?.facebook) && (
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'contact'
                      ? 'text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: activeTab === 'contact' ? primaryColor : undefined,
                    color: activeTab === 'contact' ? primaryColor : undefined
                  }}
                >
                  Contact
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="pt-4">
            {activeTab === 'about' && exhibitor.description && (
              <div className="prose prose-sm max-w-none text-gray-600 overflow-hidden">
                <div
                  className="
                    [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg
                    [&_video]:max-w-full [&_video]:h-auto [&_video]:rounded-lg
                    [&_iframe]:max-w-full [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-lg
                    [&_embed]:max-w-full [&_embed]:w-full
                    [&_object]:max-w-full [&_object]:w-full
                    [&_table]:max-w-full [&_table]:overflow-x-auto
                    [&>*]:break-words
                  "
                  dangerouslySetInnerHTML={{ __html: exhibitor.description }}
                />
              </div>
            )}

            {activeTab === 'media' && hasMediaTab && (
              <div className="space-y-4">
                {mediaElements.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {mediaElements.map((media, index) => (
                      <div
                        key={index}
                        className="rounded-lg overflow-hidden bg-gray-50"
                        dangerouslySetInnerHTML={{ __html: media.element }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No media content available</p>
                )}
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-4">
                {/* Contact Information */}
                {exhibitor.contact && (
                  <div className="flex items-start gap-2 text-gray-600">
                    <Building2 size={18} className="mt-0.5 flex-shrink-0" />
                    <span>{exhibitor.contact}</span>
                  </div>
                )}

                {/* Social Links */}
                {(exhibitor.socialLinks?.twitter || exhibitor.socialLinks?.linkedin || exhibitor.socialLinks?.facebook) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Connect with us</h4>
                    <div className="flex items-center gap-2">
                      {exhibitor.socialLinks?.twitter && (
                        <a
                          href={exhibitor.socialLinks?.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                          </svg>
                        </a>
                      )}
                      {exhibitor.socialLinks?.linkedin && (
                        <a
                          href={exhibitor.socialLinks?.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        </a>
                      )}
                      {exhibitor.socialLinks?.facebook && (
                        <a
                          href={exhibitor.socialLinks?.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sign-in prompt modal */}
      {showSignInPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Sign In Required</h3>
            <p className="text-gray-600">
              Please sign in to access this feature. You'll be redirected back to this page after signing in.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSignInPrompt(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  window.location.href = '/accounts/sign_in';
                }}
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};